import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Crown, ChevronDown, Menu, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { resolveAvatarId } from '../shared/AvatarPicker';
import toast from 'react-hot-toast';
import './Navbar.css';

const PLAN_LABELS: Record<string, string> = {
  free: 'Fledgling',
  storyteller: 'Storyteller',
  chronicle: 'Chronicle',
  friends_family: 'Friends & Family',
};

interface NavbarProps {
  variant?: 'default' | 'transparent' | 'branded';
  backTo?: { label: string; path: string };
}

export default function Navbar({ variant = 'default', backTo }: NavbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    await logout();
    toast.success('Logged out!');
    navigate('/');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);
  const planLabel = PLAN_LABELS[user?.plan || 'free'] || user?.plan || 'Fledgling';

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Close mobile menu on route change */
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Use child's avatar icon if set, otherwise fall back to fox
  const childAvatarId = user?.childProfiles?.[0]?.avatar;
  const resolvedAvatar = childAvatarId ? resolveAvatarId(childAvatarId) : 'fox';
  const avatarSrc = `/images/avatar-icons/${resolvedAvatar}-icon.png`;

  /* Close menus on Escape key */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <header className={`site-header ${variant === 'transparent' ? 'header-transparent' : ''}`}>
        {variant === 'branded' ? (
          /* ─── Branded: logo always visible + auth-aware right side ─── */
          <>
            <Link to="/" className="site-logo">
              <img src="/images/foxface.png" alt="Fox &amp; Quill logo" className="site-logo-fox" />
              <div className="logo-stack">
                <span className="logo-text">Fox &amp; Quill</span>
                <span className="site-tagline">Where tales become real.</span>
              </div>
            </Link>
            {user ? (
              <div className="site-right">
                <div className="user-dropdown" ref={dropdownRef}>
                  <button
                    className="user-toggle"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    aria-label="User menu"
                  >
                    <span className="user-avatar">
                      <img src={avatarSrc} alt="Your avatar" />
                    </span>
                    <ChevronDown size={16} className={`chevron ${userMenuOpen ? 'open' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="dropdown-menu" role="menu">
                      <div className="dropdown-header stacked">
                        <span className="dropdown-name">{user.displayName}</span>
                        <span className={`dropdown-plan plan-${user.plan || 'free'}`}>
                          {(user.plan === 'chronicle' || user.plan === 'friends_family') && <Crown size={14} fill="currentColor" />}
                          {planLabel}
                        </span>
                      </div>
                      <div className="dropdown-divider" />
                      <Link to="/library" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        Library
                      </Link>
                      <Link to="/settings" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        Settings
                      </Link>
                      <Link to="/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        My Orders
                      </Link>
                      {true && (
                        <Link to="/pricing" className="dropdown-item dropdown-upgrade" onClick={() => setUserMenuOpen(false)}>
                          Upgrade
                        </Link>
                      )}
                      {user.role === 'admin' && (
                        <Link to="/admin" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                          Admin
                        </Link>
                      )}
                      <button className="dropdown-item" onClick={handleLogout}>
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="guest-links">
                <Link to="/pricing">Pricing</Link>
                <Link to="/login" className="btn btn-ghost btn-small">Log In</Link>
                <Link to="/create" className="btn btn-primary btn-small">Begin Free</Link>
              </div>
            )}
          </>
        ) : user ? (
          /* ─── Authenticated: user dropdown only (right-aligned) ─── */
          <>
            <div style={{ flex: 1, display: 'flex' }}>
              {backTo && (
                <Link to={backTo.path} className="nav-back-btn" aria-label={backTo.label}>
                  <ArrowLeft size={22} />
                </Link>
              )}
            </div>
            <div className="site-right">
              {/* User dropdown */}
              <div className="user-dropdown" ref={dropdownRef}>
                <button
                  className="user-toggle"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <span className="user-avatar">
                    <img src={avatarSrc} alt="Your avatar" />
                  </span>
                  <ChevronDown size={16} className={`chevron ${userMenuOpen ? 'open' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div className="dropdown-menu" role="menu">
                    <div className="dropdown-header stacked">
                      <span className="dropdown-name">{user.displayName}</span>
                      <span className={`dropdown-plan plan-${user.plan || 'free'}`}>
                        {(user.plan === 'chronicle' || user.plan === 'friends_family') && <Crown size={14} fill="currentColor" />}
                        {planLabel}
                      </span>
                    </div>
                    <div className="dropdown-divider" />
                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Settings
                    </Link>
                    <Link
                      to="/orders"
                      className="dropdown-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      My Orders
                    </Link>
                    {/* Always show Upgrade for testing */}
                    {true && (
                      <Link
                        to="/pricing"
                        className="dropdown-item dropdown-upgrade"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Upgrade
                      </Link>
                    )}
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        className="dropdown-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    )}
                    <button className="dropdown-item" onClick={handleLogout}>
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-haspopup="menu"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </>
        ) : (
          /* ─── Guest: logo + public links + auth buttons ─── */
          <>
          <Link to="/" className="site-logo">
            <img src="/images/foxface.png" alt="Fox &amp; Quill logo" className="site-logo-fox" />
            <div className="logo-stack">
              <span className="logo-text">Fox &amp; Quill</span>
              <span className="site-tagline">Where tales become real.</span>
            </div>
          </Link>
          <div className="guest-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <Link to="/login" className="btn btn-ghost btn-small">Log In</Link>
            <Link to="/create" className="btn btn-primary btn-small">Begin Free</Link>
          </div>
          </>
        )}
      </header>

      {/* Mobile slide-down menu (authenticated only) */}
      {user && mobileMenuOpen && (
        <>
        <div className="mobile-menu-backdrop" onClick={() => setMobileMenuOpen(false)} />
        <nav className="mobile-menu" aria-label="Mobile navigation">
          <Link to="/library" className={`mobile-link ${isActive('/library') ? 'active' : ''}`}>
            Library
          </Link>
          <div className="mobile-divider" />
          <div className="mobile-user">
            <span className="dropdown-name">{user.displayName}</span>
            <span className={`dropdown-plan plan-${user.plan || 'free'}`}>
              {(user.plan === 'chronicle' || user.plan === 'friends_family') && <Crown size={14} fill="currentColor" />}
              {planLabel}
            </span>
          </div>
          <Link to="/settings" className={`mobile-link ${isActive('/settings') ? 'active' : ''}`}>
            Settings
          </Link>
          <Link to="/orders" className={`mobile-link ${isActive('/orders') ? 'active' : ''}`}>
            My Orders
          </Link>
          {/* Always show Upgrade for testing */}
          {true && (
            <Link to="/pricing" className={`mobile-link ${isActive('/pricing') ? 'active' : ''}`}>
              Upgrade
            </Link>
          )}
          {user.role === 'admin' && (
            <Link to="/admin" className={`mobile-link ${isActive('/admin') ? 'active' : ''}`}>
              Admin
            </Link>
          )}
          <button className="mobile-link" onClick={handleLogout}>
            Log out
          </button>
        </nav>
        </>
      )}
    </>
  );
}
